import * as yup from "yup";
import { useState } from "react";
import { states } from "./utils";
import { toast } from "react-toastify";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { Dropdown, Loader } from "semantic-ui-react";
import { PhotoIcon } from "@heroicons/react/24/solid";
import { registerNotary } from "../../actions/register";
import useBreakpoint from "@restart/hooks/useBreakpoint";
import { Formik, Form, ErrorMessage, Field } from "formik";

import "semantic-ui-css/semantic.min.css";
import "semantic-ui-css/components/icon.min.css";
import "semantic-ui-css/components/item.min.css";
import "semantic-ui-css/components/list.min.css";
import "semantic-ui-css/components/menu.min.css";
import "semantic-ui-css/components/label.min.css";
import "semantic-ui-css/components/dropdown.min.css";
import "semantic-ui-css/components/transition.min.css";

const validationSchema = yup.object({
  first_name: yup.string().required("This field is required"),
  last_name: yup.string().required("This field is required"),
  phone_number: yup
    .string()
    .min(14, "Must be 10 digits long")
    .required("This field is required"),
  identification_number: yup.string().required("This field is required"),
  state_jurisdiction: yup
    .array()
    .min(1, "This field must have at least 1 state selected")
    .required("Provide at least one state"),
  email_address: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  confirm_email: yup
    .string()
    .oneOf([yup.ref("email_address"), undefined], "Emails don't match")
    .required("This field is required"),
  password: yup.string().required("Password required"),
  confirm_password: yup
    .string()
    .oneOf([yup.ref("password"), undefined], "Passwords don't match")
    .required("This field is mandatory"),
  street_address: yup.string().required("Address required"),
  city: yup.string().required("City is required"),
  state: yup.string(),
  zip_code: yup
    .string()
    .min(5, "Must be 5 digits long")
    .required("Zip Code is required"),
  email_docusign: yup.string().email("Invalid email format"),
});

const initialValues = {
  first_name: "",
  last_name: "",
  phone_number: "",
  identification_number: "",
  state_jurisdiction: "",
  email_address: "",
  confirm_email: "",
  password: "",
  confirm_password: "",
  street_address: "",
  city: "",
  state: "",
  zip_code: "",
  company_name: "",
  company_state: "",
  company_email: "",
  company_city: "",
  company_phone_number: "",
  company_zip_code: "",
  company_street_address: "",
  email_docusign: "",
};

// type User = {
//   email: string;
//   password: string;
//   confirmPassword: string;
// };

interface OnRegisterProps {
  setUser: (value: string) => void;
  user?: string;
  isAdmin?: boolean;
}

function Register(notary: OnRegisterProps) {
  // const [creds, setCreds] = useState<User>({
  //   email: "",
  //   password: "",
  //   confirmPassword: "",
  // });
  const { isAdmin } = notary;
  const [idType, setIdType] = useState("SSN");
  const [fileName, setFileName] = useState<any>(null);
  const [contactState, setContactState] = useState("AL");
  const [submitting, setSubmitting] = useState(false);
  const [idFormat, setIdFormat] = useState("000-00-0000");
  const [error, setError] = useState("");
  const [docusignEmail, setDocusignEmail] = useState(false);
  const isSmall = useBreakpoint("sm", "down");
  const navigate = useNavigate();

  function handleSubmit(values: any) {
    setSubmitting(true);
    const payload = {
      user: {
        username: values.email_address,
        email: values.email_address,
        password: values.password,
      },
      // Notary profile data
      notary_profile: {
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone_number,
        jurisdiction: values.state_jurisdiction.join(),
        spanish_speaking: true,
      },
      // Notary credentials data
      notary_credentials: {
        license_number: values.identification_number,
        ron_approval_number: "RON654321",
        ssn: idType === "SSN" ? values.identification_number : "",
        ein: idType === "EIN" ? values.identification_number : "",
      },
      // Notary address data
      notary_address: {
        address1: values.street_address,
        city: values.city,
        state: contactState,
        zip_code: values.zip_code,
      },
    };

    registerNotary(payload)
      .then((e) => {
        // setCreds({ email: "", password: "", confirmPassword: "" });
        // setUser.setUser(creds.email);
        toast.success(
          "User successfully created. We have sent you an email to activate your account",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          }
        );
        setTimeout(() => {
          setSubmitting(false);
          // For now we want to return the user back to the admin
          navigate("/admin");
        }, 3000);
      })
      .catch((e) => {
        setSubmitting(false);
        toast.error(
          `Something is wrong: ` + getErrorMessage(e.response?.data),
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          }
        );
      });
  }

  function formatPhoneNumber(phoneNumberString: string, setFieldValue: any) {
    let cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      setFieldValue(
        "phone_number",
        "(" + match[1] + ") " + match[2] + "-" + match[3]
      );
      return "(" + match[1] + ") " + match[2] + "-" + match[3];
    }
    return null;
  }

  function formatSSN(ssnString: string, setFieldValue: any) {
    let cleanedSSN = ("" + ssnString).replace(/\D/g, "");
    let matchSSN = cleanedSSN.match(/^(\d{3})(\d{2})(\d{4})$/);
    if (matchSSN) {
      setFieldValue(
        "identification_number",
        matchSSN[1] + "-" + matchSSN[2] + "-" + matchSSN[3]
      );

      return matchSSN[1] + "-" + matchSSN[2] + "-" + matchSSN[3];
    }
    setFieldValue("identification_number", "");
    setError("Invalid SSN format");
    return null;
  }

  function formatEin(einString: string, setFieldValue: any) {
    let cleaned = ("" + einString).replace(/\D/g, "");
    let match = cleaned.match(/^(\d{2})(\d{7})$/);
    if (match) {
      setFieldValue("identification_number", match[1] + "-" + match[2]);
      return match[1] + "-" + match[2];
    }
    setFieldValue("identification_number", "");
    setError("Invalid EIN format");
    return null;
  }

  function getErrorMessage(error: any) {
    if (!error) {
      return "please try again!";
    }

    const ssn = error.license?.ssn ? error.license.ssn : undefined;
    const ein = error.license?.ein ? error.license.ein : undefined;

    if (ssn) {
      return ssn;
    }

    if (ein) {
      return ein;
    }

    return "please try again!";
  }

  return (
    <div className={`grid grid-cols-12 gap-0 ${isAdmin ? "w-full" : ""}`}>
      {!isAdmin && (
        <div className="h-auto col-span-12 md:col-span-4 bg-sky-50">
          <div className="flex flex-col md:h-screen justify-between mx-16 mt-4">
            <Header />
            <main className="mb-6 md:mb-auto md:h-10">
              <div className="flex text-3xl font-bold w-75 flex-wrap max-w-sm text-slate-400">
                Let's get started on your Notary Account
              </div>
            </main>
            <footer className="mb-6 md:h-10"></footer>
          </div>
        </div>
      )}

      <div
        className={`col-span-12 ${
          isAdmin ? "md:col-span-12" : "md:col-span-8"
        }`}
      >
        {submitting ? (
          <div
            className={`col-span-12 md:col-span-8`}
            style={{ maxWidth: "1000px" }}
          >
            <Loader
              style={{
                display: "block",
                left: `${isSmall ? "50%" : "65%"}`,
                maxWidth: "1000px",
              }}
            >
              <div>{"Creating your Account..."}</div>
            </Loader>
          </div>
        ) : (
          <div
            className={`m-8 bg-white rounded-lg px-6 py-8 ring-1 ring-slate-900/5 shadow-xl`}
            style={{ maxWidth: `${isAdmin ? "auto" : "1000px"}` }}
          >
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <div className="space-y-12">
                    <div className="border-b border-gray-900/10 pb-12">
                      <h2 className="text-base font-semibold leading-7 text-gray-900">
                        Notary Registration
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        If you are approved, you will be able to{" "}
                        <span className="font-bold">
                          Execute Closing Documents.
                        </span>
                      </p>

                      <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-2">
                          <label
                            htmlFor="first_name"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            First Name
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="first_name"
                              id="first_name"
                              autoComplete="given-name"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="first_name" />
                          </span>
                        </div>

                        <div className="sm:col-span-2">
                          <label
                            htmlFor="last_name"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Last Name
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="last_name"
                              id="last_name"
                              autoComplete="family-name"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="last_name" />
                          </span>
                        </div>

                        <div className="sm:col-span-2">
                          <label
                            htmlFor="last_name"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Phone Number
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="phone_number"
                              id="phone_number"
                              placeholder="(333) 444-5555"
                              onBlur={(e: any) =>
                                formatPhoneNumber(e.target.value, setFieldValue)
                              }
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="phone_number" />
                          </span>
                        </div>

                        <div className="sm:col-span-2">
                          <label
                            htmlFor="Id"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            ID
                          </label>
                          <div className="mt-2">
                            <select
                              id="select_id"
                              name="select_id"
                              autoComplete="select_id"
                              onChange={(e) => {
                                setIdType(e.target.value);
                                setError("");
                                e.target.value === "SSN"
                                  ? setIdFormat("000-00-0000")
                                  : setIdFormat("00-0000000");
                                setFieldValue("identification_number", "");
                              }}
                              className="block w-full rounded-md border-0 p-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            >
                              <option value="SSN">SSN</option>
                              <option value="EIN">EIN</option>
                            </select>
                          </div>
                        </div>

                        <div className="sm:col-span-4">
                          <label
                            htmlFor="identification_number"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            No.
                          </label>
                          <div className="mt-2">
                            <Field
                              id="identification_number"
                              name="identification_number"
                              type="text"
                              placeholder={idFormat}
                              onFocus={() => setError("")}
                              onBlur={(e: any) => {
                                idType === "SSN"
                                  ? formatSSN(e.target.value, setFieldValue)
                                  : formatEin(e.target.value, setFieldValue);
                              }}
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <span>{error}</span>
                            <ErrorMessage name="identification_number" />
                          </span>
                        </div>

                        <div className="sm:col-span-4">
                          <div className="sm:me-0 md:me-5">
                            <label
                              htmlFor="state_jurisdiction"
                              className="block text-sm font-medium leading-6 text-gray-900"
                            >
                              State Jusrisdiction
                            </label>
                            <div className="mt-2">
                              <Dropdown
                                id={"state_jurisdiction"}
                                name={"state_jurisdiction"}
                                placeholder="States"
                                fluid
                                multiple
                                selection
                                options={states}
                                onChange={(_, data) =>
                                  setFieldValue(
                                    "state_jurisdiction",
                                    data.value
                                  )
                                }
                              />
                            </div>
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="state_jurisdiction" />
                          </span>
                        </div>

                        <div className="sm:col-span-3">
                          <label
                            htmlFor="email_address"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Email Address
                          </label>
                          <div className="mt-2">
                            <Field
                              id="email_address"
                              name="email_address"
                              type="email"
                              autoComplete="email_address"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="email_address" />
                          </span>
                        </div>

                        <div className="sm:col-span-3">
                          <label
                            htmlFor="confirm_email"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Confirm Email Address
                          </label>
                          <div className="mt-2">
                            <Field
                              id="confirm_email"
                              name="confirm_email"
                              type="email"
                              autoComplete="confirm_email"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="confirm_email" />
                          </span>
                        </div>

                        <div className="sm:col-span-3">
                          <label
                            htmlFor="password"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Password
                          </label>
                          <div className="mt-2">
                            <Field
                              type="password"
                              name="password"
                              id="password"
                              autoComplete="given-name"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="password" />
                          </span>
                        </div>

                        <div className="sm:col-span-3">
                          <label
                            htmlFor="confirm_password"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Confirm Password
                          </label>
                          <div className="mt-2">
                            <Field
                              type="password"
                              name="confirm_password"
                              id="confirm_password"
                              autoComplete="family-name"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="confirm_password" />
                          </span>
                        </div>

                        <div className="col-span-full">
                          <label
                            htmlFor="street_address"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Street Address
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="street_address"
                              id="street_address"
                              autoComplete="street_address"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="street_address" />
                          </span>
                        </div>

                        <div className="sm:col-span-2 sm:col-start-1">
                          <label
                            htmlFor="city"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            City
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="city"
                              id="city"
                              autoComplete="city"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="city" />
                          </span>
                        </div>

                        <div className="sm:col-span-2">
                          <label
                            htmlFor="state"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            State
                          </label>
                          <div className="mt-2">
                            <Field
                              name="state"
                              as="select"
                              value={contactState}
                              onChange={(e: any) =>
                                setContactState(e.target.value)
                              }
                              className="h-9 block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            >
                              <option selected value="AL">
                                Alabama
                              </option>
                              <option value="AK">Alaska</option>
                              <option value="AZ">Arizona</option>
                              <option value="AR">Arkansas</option>
                              <option value="CA">California</option>
                              <option value="CO">Colorado</option>
                              <option value="CT">Connecticut</option>
                              <option value="DE">Delaware</option>
                              <option value="DC">District Of Columbia</option>
                              <option value="FL">Florida</option>
                              <option value="GA">Georgia</option>
                              <option value="HI">Hawaii</option>
                              <option value="ID">Idaho</option>
                              <option value="IL">Illinois</option>
                              <option value="IN">Indiana</option>
                              <option value="IA">Iowa</option>
                              <option value="KS">Kansas</option>
                              <option value="KY">Kentucky</option>
                              <option value="LA">Louisiana</option>
                              <option value="ME">Maine</option>
                              <option value="MD">Maryland</option>
                              <option value="MA">Massachusetts</option>
                              <option value="MI">Michigan</option>
                              <option value="MN">Minnesota</option>
                              <option value="MS">Mississippi</option>
                              <option value="MO">Missouri</option>
                              <option value="MT">Montana</option>
                              <option value="NE">Nebraska</option>
                              <option value="NV">Nevada</option>
                              <option value="NH">New Hampshire</option>
                              <option value="NJ">New Jersey</option>
                              <option value="NM">New Mexico</option>
                              <option value="NY">New York</option>
                              <option value="NC">North Carolina</option>
                              <option value="ND">North Dakota</option>
                              <option value="OH">Ohio</option>
                              <option value="OK">Oklahoma</option>
                              <option value="OR">Oregon</option>
                              <option value="PA">Pennsylvania</option>
                              <option value="RI">Rhode Island</option>
                              <option value="SC">South Carolina</option>
                              <option value="SD">South Dakota</option>
                              <option value="TN">Tennessee</option>
                              <option value="TX">Texas</option>
                              <option value="UT">Utah</option>
                              <option value="VT">Vermont</option>
                              <option value="VA">Virginia</option>
                              <option value="WA">Washington</option>
                              <option value="WV">West Virginia</option>
                              <option value="WI">Wisconsin</option>
                              <option value="WY">Wyoming</option>
                            </Field>
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="state" />
                          </span>
                        </div>

                        <div className="sm:col-span-2">
                          <label
                            htmlFor="zip_code"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            ZIP Code
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="zip_code"
                              id="zip_code"
                              autoComplete="zip_code"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                          <span className="text-xs font-bold text-red-500">
                            <ErrorMessage name="zip_code" />
                          </span>
                        </div>

                        <div className="col-span-full">
                          <label
                            htmlFor="cover-photo"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            E-notary (pdf only)
                          </label>
                          <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                            <div className="text-center">
                              <PhotoIcon
                                className="mx-auto h-12 w-12 text-gray-300"
                                aria-hidden="true"
                              />
                              <div className="mt-4 flex text-sm leading-6 text-gray-600">
                                <label
                                  htmlFor="enotary"
                                  className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                                >
                                  <span>
                                    {fileName
                                      ? fileName
                                      : "Upload your e-notary certificate"}
                                  </span>
                                  <input
                                    id="enotary"
                                    name="enotary"
                                    type="file"
                                    className="sr-only"
                                    onChange={(e) => {
                                      e.target.files !== null &&
                                      e.target.files.length > 0
                                        ? setFileName(e.target.files[0].name)
                                        : setFileName(null);
                                    }}
                                  />
                                </label>
                                <p className="pl-1">
                                  {fileName ? "" : "or drag and drop"}
                                </p>
                              </div>
                              <p className="text-xs leading-5 text-gray-600">
                                PDF up to 10MB
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="sm:col-span-4">
                          <div className=" space-y-10">
                            <fieldset>
                              <legend className="text-sm font-semibold leading-6 text-gray-900">
                                DocuSign
                              </legend>
                              <div className="mt-6 space-y-6">
                                <div className="relative flex gap-x-3">
                                  <div className="flex h-6 items-center">
                                    <input
                                      id="docusign"
                                      name="docusign"
                                      type="checkbox"
                                      onChange={(e) => {
                                        setDocusignEmail(e.target.checked);
                                      }}
                                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                    />
                                  </div>
                                  <div className="text-sm leading-6">
                                    <label
                                      htmlFor="docusign"
                                      className="font-medium text-gray-900"
                                    >
                                      Do you use a seperate docusign email?
                                    </label>
                                    <div
                                      className={`${
                                        docusignEmail ? " " : "hidden"
                                      } mt-2`}
                                    >
                                      <label
                                        htmlFor="email_docusign"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                      >
                                        DocuSign Email
                                      </label>
                                      <div className="mt-2">
                                        <Field
                                          id="email_docusign"
                                          name="email_docusign"
                                          type="email"
                                          placeholder="docusign@email.com"
                                          autoComplete="email_docusign"
                                          className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </fieldset>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* <div className="border-b border-gray-900/10 pb-12">
                      <h2 className="text-base font-semibold leading-7 text-gray-900">
                        Company Information
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-gray-600"></p>

                      <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                          <label
                            htmlFor="company_name"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Company Name
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="company_name"
                              id="company_name"
                              autoComplete="given-name"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>

                        <div className="col-span-full">
                          <label
                            htmlFor="company_street_address"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Street Address
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="company_street_address"
                              id="company_street_address"
                              autoComplete="company_street_address"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>

                        <div className="sm:col-span-2 sm:col-start-1">
                          <label
                            htmlFor="company_city"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            City
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="company_city"
                              id="company_city"
                              autoComplete="company_city"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>

                        <div className="sm:col-span-2">
                          <label
                            htmlFor="company_state"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            State
                          </label>
                          <div className="mt-2">
                            <Field
                              name="company_state"
                              as="select"
                              value={companyState}
                              onChange={(e: any) =>
                                setCompanyState(e.target.value)
                              }
                              className="h-9 block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            >
                              <option selected value="AL">
                                Alabama
                              </option>
                              <option value="AK">Alaska</option>
                              <option value="AZ">Arizona</option>
                              <option value="AR">Arkansas</option>
                              <option value="CA">California</option>
                              <option value="CO">Colorado</option>
                              <option value="CT">Connecticut</option>
                              <option value="DE">Delaware</option>
                              <option value="DC">District Of Columbia</option>
                              <option value="FL">Florida</option>
                              <option value="GA">Georgia</option>
                              <option value="HI">Hawaii</option>
                              <option value="ID">Idaho</option>
                              <option value="IL">Illinois</option>
                              <option value="IN">Indiana</option>
                              <option value="IA">Iowa</option>
                              <option value="KS">Kansas</option>
                              <option value="KY">Kentucky</option>
                              <option value="LA">Louisiana</option>
                              <option value="ME">Maine</option>
                              <option value="MD">Maryland</option>
                              <option value="MA">Massachusetts</option>
                              <option value="MI">Michigan</option>
                              <option value="MN">Minnesota</option>
                              <option value="MS">Mississippi</option>
                              <option value="MO">Missouri</option>
                              <option value="MT">Montana</option>
                              <option value="NE">Nebraska</option>
                              <option value="NV">Nevada</option>
                              <option value="NH">New Hampshire</option>
                              <option value="NJ">New Jersey</option>
                              <option value="NM">New Mexico</option>
                              <option value="NY">New York</option>
                              <option value="NC">North Carolina</option>
                              <option value="ND">North Dakota</option>
                              <option value="OH">Ohio</option>
                              <option value="OK">Oklahoma</option>
                              <option value="OR">Oregon</option>
                              <option value="PA">Pennsylvania</option>
                              <option value="RI">Rhode Island</option>
                              <option value="SC">South Carolina</option>
                              <option value="SD">South Dakota</option>
                              <option value="TN">Tennessee</option>
                              <option value="TX">Texas</option>
                              <option value="UT">Utah</option>
                              <option value="VT">Vermont</option>
                              <option value="VA">Virginia</option>
                              <option value="WA">Washington</option>
                              <option value="WV">West Virginia</option>
                              <option value="WI">Wisconsin</option>
                              <option value="WY">Wyoming</option>
                            </Field>
                          </div>
                        </div>

                        <div className="sm:col-span-2">
                          <label
                            htmlFor="company_zip_code"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            ZIP Code
                          </label>
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="company_zip_code"
                              id="company_zip_code"
                              autoComplete="company_zip_code"
                              className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>
                      </div>
                    </div> */}
                  </div>

                  <div className="mt-6 flex items-center justify-end gap-x-6">
                    <button
                      type="button"
                      className="text-sm font-semibold leading-6 text-gray-900"
                      onClick={() => window.history.back()}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Save
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </div>
    </div>
  );
}

export default Register;
